import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Radio, Tv, FileText, BarChart3, Shield, Globe, Play, Square, Plus, ChevronRight, AlertTriangle, CheckCircle, XCircle, Clock, RefreshCw, Zap, Signal, Film, Users, DollarSign, Activity, Layers, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge component
const StatusBadge = ({ status, size = 'sm' }) => {
  const colors = {
    live: 'bg-green-500', active: 'bg-green-500', signed: 'bg-green-500', approved: 'bg-green-500',
    idle: 'bg-gray-500', draft: 'bg-gray-500', credentials_missing: 'bg-gray-500',
    demo: 'bg-amber-500', pending_rights: 'bg-amber-500', qc_review: 'bg-amber-500', rights_review: 'bg-amber-500', editorial_review: 'bg-amber-500',
    rejected: 'bg-red-500', blocked: 'bg-red-500', declined: 'bg-red-500', voided: 'bg-red-500',
    sent: 'bg-blue-500', internal_review: 'bg-blue-500', uploaded: 'bg-blue-500',
    scheduled: 'bg-purple-500', credentials_configured: 'bg-green-500',
  };
  const color = colors[status] || 'bg-gray-500';
  return (
    <span className={`inline-flex items-center gap-1.5 ${size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-full ${color} text-white font-medium`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full bg-white ${status === 'live' ? 'animate-pulse' : ''}`} />
      {status.replace(/_/g, ' ').toUpperCase()}
    </span>
  );
};

// KPI Card
const KPICard = ({ label, value, icon: Icon, color = 'text-white' }) => (
  <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-gray-700/50 rounded-xl p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      {Icon && <Icon size={18} className="text-gray-500" />}
    </div>
    <div className={`text-3xl font-black ${color}`}>{value ?? '—'}</div>
  </div>
);

// Sub-tab button
const SubTab = ({ label, active, onClick, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${active ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
    data-testid={`subtab-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    {Icon && <Icon size={16} />}
    {label}
  </button>
);

// === BROADCAST OS TAB ===
const BroadcastOSTab = ({ dashboard, authHeader, onRefresh }) => {
  const [liveChannels, setLiveChannels] = useState(dashboard?.live_channels || []);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [newChannel, setNewChannel] = useState({ name: '', description: '', type: 'mixed', market: 'national' });
  const [error, setError] = useState('');

  // Sync liveChannels when dashboard refreshes
  useEffect(() => {
    if (dashboard?.live_channels) {
      setLiveChannels(dashboard.live_channels);
    }
  }, [dashboard]);

  const handleAddChannel = async () => {
    setError('');
    try {
      const res = await axios.post(`${API}/broadcast/live-channels`, newChannel, authHeader);
      setLiveChannels([...liveChannels, res.data]);
      setShowAddChannel(false);
      setNewChannel({ name: '', description: '', type: 'mixed', market: 'national' });
    } catch (e) { setError(e.response?.data?.detail || 'Failed to create channel'); }
  };

  const handleToggleLive = async (channelId) => {
    setError('');
    try {
      const res = await axios.post(`${API}/broadcast/live-channels/${channelId}/go-live`, {}, authHeader);
      setLiveChannels(liveChannels.map(ch => ch.id === channelId ? { ...ch, is_live: res.data.is_live, status: res.data.status } : ch));
    } catch (e) { setError(e.response?.data?.detail || 'Failed to toggle live status'); }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Delete this channel?')) return;
    setError('');
    try {
      await axios.delete(`${API}/broadcast/live-channels/${channelId}`, authHeader);
      setLiveChannels(liveChannels.filter(ch => ch.id !== channelId));
    } catch (e) { setError(e.response?.data?.detail || 'Failed to delete channel'); }
  };

  return (
    <div className="space-y-6" data-testid="broadcast-os-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white flex items-center gap-3">
            <Radio className="text-red-500" size={24} /> Broadcast Operations
          </h2>
          <p className="text-gray-400 mt-1">Live channels, content assets, campaigns and production controls</p>
        </div>
        <button onClick={onRefresh} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="refresh-broadcast">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center justify-between bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-lg">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-300 ml-2">✕</button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Viewers" value={dashboard?.metrics?.viewers} icon={Users} color="text-blue-400" />
        <KPICard label="Content Assets" value={dashboard?.metrics?.content_assets} icon={Film} color="text-purple-400" />
        <KPICard label="Live Channels" value={dashboard?.metrics?.live_channels} icon={Signal} color="text-green-400" />
        <KPICard label="Campaigns" value={dashboard?.metrics?.campaigns} icon={DollarSign} color="text-amber-400" />
      </div>

      {/* Channel Control */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Tv size={20} /> Channel Control</h3>
          <button onClick={() => setShowAddChannel(true)} className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-2 rounded-lg" data-testid="add-live-channel-btn">
            <Plus size={14} /> Add Channel
          </button>
        </div>

        {showAddChannel && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-4 space-y-3">
            <input value={newChannel.name} onChange={e => setNewChannel({...newChannel, name: e.target.value})} placeholder="Channel name" className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="new-channel-name" />
            <input value={newChannel.description} onChange={e => setNewChannel({...newChannel, description: e.target.value})} placeholder="Description" className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            <div className="flex gap-3">
              <select value={newChannel.type} onChange={e => setNewChannel({...newChannel, type: e.target.value})} className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="new-channel-type">
                <option value="mixed">Mixed (Live + Pre-recorded)</option>
                <option value="live">Live Only</option>
                <option value="pre-recorded">Pre-recorded Only</option>
              </select>
              <select value={newChannel.market} onChange={e => setNewChannel({...newChannel, market: e.target.value})} className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm">
                <option value="national">National</option>
                <option value="regional">Regional</option>
                <option value="local">Local</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleAddChannel} className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded" data-testid="save-channel-btn">Create Channel</button>
              <button onClick={() => setShowAddChannel(false)} className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {liveChannels.length === 0 && <p className="text-gray-500 text-sm">No live channels configured</p>}
          {liveChannels.map(ch => (
            <div key={ch.id} className="flex items-center justify-between bg-gray-900/50 border border-gray-700/30 rounded-lg px-4 py-3 hover:border-gray-600 transition-colors" data-testid={`live-channel-${ch.id}`}>
              <div className="flex items-center gap-4">
                <StatusBadge status={ch.status || (ch.is_live ? 'live' : 'idle')} />
                <div>
                  <span className="text-white font-medium">{ch.name}</span>
                  <span className="text-gray-500 text-xs ml-2">{ch.type} · {ch.market}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-xs">{ch.playlist?.length || 0} items</span>
                <button onClick={() => handleToggleLive(ch.id)} className={`px-3 py-1.5 rounded text-xs font-medium ${ch.is_live ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30' : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'}`} data-testid={`toggle-live-${ch.id}`}>
                  {ch.is_live ? <><Square size={12} className="inline mr-1" />Stop</> : <><Play size={12} className="inline mr-1" />Go Live</>}
                </button>
                <button onClick={() => handleDeleteChannel(ch.id)} className="text-gray-500 hover:text-red-400 text-xs px-2 py-1" data-testid={`delete-channel-${ch.id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Production Actions */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Zap size={20} /> Production Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Creator Upload', icon: Upload, desc: 'New content submission', action: () => alert('Creator Upload: Navigate to Content > Movies or TV Shows to upload new content.') },
            { label: 'Open Scheduler', icon: Clock, desc: 'Program scheduling', action: () => alert('Scheduler: Navigate to Channels > Schedule to manage program scheduling.') },
            { label: 'Start Workflow', icon: Activity, desc: 'Content approval flow', action: () => { document.querySelector('[data-testid="subtab-workflows"]')?.click(); } },
            { label: 'Emergency Slate', icon: AlertTriangle, desc: 'Backup broadcast', action: () => { if (window.confirm('Activate emergency slate? This will pause all live channels and display a placeholder.')) { alert('Emergency slate activated (demo mode). In production, this would switch all MediaLive channels to a backup slate.'); } } },
          ].map(action => (
            <button key={action.label} onClick={action.action} className="flex flex-col items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg p-4 hover:border-amber-500/50 hover:bg-gray-900/80 transition-all group" data-testid={`action-${action.label.toLowerCase().replace(/\s/g, '-')}`}>
              <action.icon size={20} className="text-amber-500 group-hover:text-amber-400" />
              <span className="text-white text-sm font-medium">{action.label}</span>
              <span className="text-gray-500 text-xs">{action.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// === LEGAL PORTAL TAB ===
const LegalPortalTab = ({ dashboard, authHeader }) => {
  const [contracts, setContracts] = useState([]);
  const [showAddContract, setShowAddContract] = useState(false);
  const [newContract, setNewContract] = useState({ title: '', territory: '', terms: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await axios.get(`${API}/broadcast/contracts`, authHeader);
        setContracts(res.data);
      } catch (e) { setError('Failed to load contracts'); }
    };
    fetchContracts();
  }, []);

  const handleAddContract = async () => {
    setError('');
    try {
      const res = await axios.post(`${API}/broadcast/contracts`, newContract, authHeader);
      setContracts([res.data, ...contracts]);
      setShowAddContract(false);
      setNewContract({ title: '', territory: '', terms: '' });
    } catch (e) { setError(e.response?.data?.detail || 'Failed to create contract'); }
  };

  const handleStatusChange = async (contractId, newStatus) => {
    setError('');
    try {
      const res = await axios.put(`${API}/broadcast/contracts/${contractId}/status`, { status: newStatus }, authHeader);
      setContracts(contracts.map(c => c.id === contractId ? res.data : c));
    } catch (e) { setError(e.response?.data?.detail || 'Failed to update contract status'); }
  };

  return (
    <div className="space-y-6" data-testid="legal-portal-tab">
      <div>
        <h2 className="text-2xl font-black text-white flex items-center gap-3">
          <Shield className="text-amber-500" size={24} /> Legal System Portal
        </h2>
        <p className="text-gray-400 mt-1">Contracts, rights clearance, talent management, and digital signatures</p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center justify-between bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-lg">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-300 ml-2">✕</button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Active Contracts" value={contracts.filter(c => !['declined', 'voided', 'expired'].includes(c.status)).length} icon={FileText} color="text-amber-400" />
        <KPICard label="Signed" value={contracts.filter(c => c.status === 'signed').length} icon={CheckCircle} color="text-green-400" />
        <KPICard label="Pending Review" value={contracts.filter(c => ['draft', 'internal_review'].includes(c.status)).length} icon={Clock} color="text-blue-400" />
        <KPICard label="Signature Mode" value={dashboard?.integrations?.signatures?.mode === 'demo' ? 'Demo' : 'Live'} icon={Shield} color="text-purple-400" />
      </div>

      {/* Contracts */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Contracts</h3>
          <button onClick={() => setShowAddContract(true)} className="flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white text-sm px-3 py-2 rounded-lg" data-testid="add-contract-btn">
            <Plus size={14} /> New Contract
          </button>
        </div>

        {showAddContract && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-4 space-y-3">
            <input value={newContract.title} onChange={e => setNewContract({...newContract, title: e.target.value})} placeholder="Contract title" className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="contract-title-input" />
            <input value={newContract.territory} onChange={e => setNewContract({...newContract, territory: e.target.value})} placeholder="Territory (e.g. US, Global)" className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            <textarea value={newContract.terms} onChange={e => setNewContract({...newContract, terms: e.target.value})} placeholder="Terms" rows={3} className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            <div className="flex gap-2">
              <button onClick={handleAddContract} className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded" data-testid="save-contract-btn">Create</button>
              <button onClick={() => setShowAddContract(false)} className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {contracts.length === 0 && <p className="text-gray-500 text-sm">No contracts yet</p>}
          {contracts.map(c => (
            <div key={c.id} className="flex items-center justify-between bg-gray-900/50 border border-gray-700/30 rounded-lg px-4 py-3">
              <div className="flex items-center gap-4">
                <StatusBadge status={c.status} />
                <div>
                  <span className="text-white font-medium">{c.title}</span>
                  {c.territory && <span className="text-gray-500 text-xs ml-2">{c.territory}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {c.status === 'draft' && (
                  <button onClick={() => handleStatusChange(c.id, 'internal_review')} className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 bg-blue-500/10 rounded" data-testid={`contract-review-${c.id}`}>Send for Review</button>
                )}
                {c.status === 'internal_review' && (
                  <button onClick={() => handleStatusChange(c.id, 'sent')} className="text-green-400 hover:text-green-300 text-xs px-2 py-1 bg-green-500/10 rounded" data-testid={`contract-send-${c.id}`}>Send to Signer</button>
                )}
                {c.status === 'sent' && (
                  <button onClick={() => handleStatusChange(c.id, 'signed')} className="text-amber-400 hover:text-amber-300 text-xs px-2 py-1 bg-amber-500/10 rounded" data-testid={`contract-sign-${c.id}`}>Mark Signed</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legal Workflow Reference */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Legal Workflows</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left py-2 px-3">Workflow</th>
                <th className="text-left py-2 px-3">Steps</th>
                <th className="text-left py-2 px-3">Requirements</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800"><td className="py-2 px-3 font-medium">Contract Generation</td><td className="py-2 px-3">Template → Fill → Review → Approve</td><td className="py-2 px-3">Approved template, authorized signer</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3 font-medium">Digital Signature</td><td className="py-2 px-3">Envelope → Send → View → Sign</td><td className="py-2 px-3">DocuSign or demo mode</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3 font-medium">Program Publishing</td><td className="py-2 px-3">Rights check → Territory → Platform → Monetization</td><td className="py-2 px-3">Rights record with all fields cleared</td></tr>
              <tr><td className="py-2 px-3 font-medium">Local Feed Activation</td><td className="py-2 px-3">License → Carriage → Blackout → Approval</td><td className="py-2 px-3">Signed carriage agreement</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Integration Card component (used by ConnectionsTab)
const IntegrationCard = ({ name, data, icon: Icon }) => {
  const mode = data?.mode || 'unknown';
  const connected = data?.connected !== false;
  return (
    <div className={`bg-gray-800/50 border rounded-xl p-5 ${connected ? 'border-gray-700/50' : 'border-amber-500/30'}`} data-testid={`integration-${name.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-gray-400" />}
          <span className="text-white font-medium">{name}</span>
        </div>
        <StatusBadge status={mode} />
      </div>
      {data?.message && <p className="text-gray-500 text-xs">{data.message}</p>}
      {data?.provider && <p className="text-gray-500 text-xs">Provider: {data.provider}</p>}
      {data?.state && <p className="text-gray-500 text-xs">State: {data.state}</p>}
      {data?.playback_url && <p className="text-gray-500 text-xs truncate">URL: {data.playback_url}</p>}
    </div>
  );
};

// App Store Card component (used by ConnectionsTab)
const AppStoreCard = ({ platform, status }) => (
  <div className="flex items-center justify-between bg-gray-900/50 border border-gray-700/30 rounded-lg px-4 py-3">
    <span className="text-white text-sm">{platform}</span>
    <StatusBadge status={status} size="sm" />
  </div>
);

// === CONNECTIONS TAB ===
const ConnectionsTab = ({ dashboard }) => {
  const integrations = dashboard?.integrations || {};

  return (
    <div className="space-y-6" data-testid="connections-tab">
      <div>
        <h2 className="text-2xl font-black text-white flex items-center gap-3">
          <Globe className="text-blue-500" size={24} /> Production Connections
        </h2>
        <p className="text-gray-400 mt-1">Integration status for all external services and vendor connections</p>
      </div>

      {/* System Mode Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${dashboard?.system_mode === 'demo' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-green-500/10 border-green-500/30 text-green-400'}`}>
        <AlertTriangle size={18} />
        <span className="text-sm font-medium">
          System running in <strong>{dashboard?.system_mode?.toUpperCase()}</strong> mode.
          {dashboard?.system_mode === 'demo' && ' All integrations use demo adapters. Add production credentials to go live.'}
        </span>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <IntegrationCard name="Live Streaming" data={integrations.streaming} icon={Radio} />
        <IntegrationCard name="Payments" data={integrations.payments} icon={DollarSign} />
        <IntegrationCard name="Digital Signatures" data={integrations.signatures} icon={FileText} />
        <IntegrationCard name="Advertising" data={integrations.advertising} icon={BarChart3} />
      </div>

      {/* App Stores */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">App Store Distribution</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <AppStoreCard platform="Apple App Store" status={integrations.app_stores?.apple || 'credentials_missing'} />
          <AppStoreCard platform="Google Play" status={integrations.app_stores?.google_play || 'credentials_missing'} />
          <AppStoreCard platform="Roku Channel Store" status={integrations.app_stores?.roku || 'credentials_missing'} />
          <AppStoreCard platform="Amazon Fire TV" status={integrations.app_stores?.fire_tv || 'credentials_missing'} />
        </div>
      </div>

      {/* Raw JSON */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Raw Integration Data</h3>
        <pre className="bg-gray-900 rounded-lg p-4 text-xs text-gray-300 overflow-x-auto max-h-64 overflow-y-auto font-mono">
          {JSON.stringify(integrations, null, 2)}
        </pre>
      </div>
    </div>
  );
};

// === WORKFLOW TAB ===
const WorkflowTab = ({ dashboard, authHeader }) => {
  const [workflows, setWorkflows] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newWf, setNewWf] = useState({ title: '', content_type: 'movie' });

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const res = await axios.get(`${API}/broadcast/workflows`, authHeader);
        setWorkflows(res.data);
      } catch (e) { console.error(e); }
    };
    fetchWorkflows();
  }, []);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/broadcast/workflows`, newWf, authHeader);
      setWorkflows([res.data, ...workflows]);
      setShowCreate(false);
      setNewWf({ title: '', content_type: 'movie' });
    } catch (e) { console.error(e); }
  };

  const advanceWorkflow = async (wfId, currentStatus) => {
    const order = ['draft', 'uploaded', 'qc_review', 'rights_review', 'editorial_review', 'approved', 'scheduled', 'live'];
    const idx = order.indexOf(currentStatus);
    if (idx < order.length - 1) {
      try {
        const res = await axios.put(`${API}/broadcast/workflows/${wfId}/status`, { status: order[idx + 1] }, authHeader);
        setWorkflows(workflows.map(w => w.id === wfId ? res.data : w));
      } catch (e) { console.error(e); }
    }
  };

  return (
    <div className="space-y-6" data-testid="workflow-tab">
      <div>
        <h2 className="text-2xl font-black text-white flex items-center gap-3">
          <Layers className="text-purple-500" size={24} /> Content Workflows
        </h2>
        <p className="text-gray-400 mt-1">Creator intake, quality review, rights clearance, editorial approval pipeline</p>
      </div>

      {/* Workflow Stages Reference */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Functional MVP Workflow</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left py-2 px-3">Stage</th>
                <th className="text-left py-2 px-3">System Action</th>
                <th className="text-left py-2 px-3">Gate</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="draft" /></td><td className="py-2 px-3">Creator submits content</td><td className="py-2 px-3">Upload complete</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="uploaded" /></td><td className="py-2 px-3">File validated, metadata extracted</td><td className="py-2 px-3">Technical QC pass</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="qc_review" /></td><td className="py-2 px-3">Quality check (resolution, audio, encoding)</td><td className="py-2 px-3">QC team approval</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="rights_review" /></td><td className="py-2 px-3">Rights records verified</td><td className="py-2 px-3">Territory + platform + dates</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="editorial_review" /></td><td className="py-2 px-3">Content reviewed for editorial standards</td><td className="py-2 px-3">Editorial approval</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="approved" /></td><td className="py-2 px-3">Content cleared for scheduling</td><td className="py-2 px-3">Schedule assignment</td></tr>
              <tr className="border-b border-gray-800"><td className="py-2 px-3"><StatusBadge status="scheduled" /></td><td className="py-2 px-3">Assigned to channel/time slot</td><td className="py-2 px-3">Go-live trigger</td></tr>
              <tr><td className="py-2 px-3"><StatusBadge status="live" /></td><td className="py-2 px-3">Available for viewer playback</td><td className="py-2 px-3">—</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Workflows */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Active Workflows</h3>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-2 rounded-lg" data-testid="start-workflow-btn">
            <Plus size={14} /> Start Workflow
          </button>
        </div>

        {showCreate && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-4 space-y-3">
            <input value={newWf.title} onChange={e => setNewWf({...newWf, title: e.target.value})} placeholder="Content title" className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="workflow-title-input" />
            <select value={newWf.content_type} onChange={e => setNewWf({...newWf, content_type: e.target.value})} className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm">
              <option value="movie">Movie</option>
              <option value="episode">TV Episode</option>
              <option value="live">Live Event</option>
            </select>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded" data-testid="create-workflow-btn">Create</button>
              <button onClick={() => setShowCreate(false)} className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {workflows.length === 0 && <p className="text-gray-500 text-sm">No active workflows. Start one to begin the content pipeline.</p>}
          {workflows.map(wf => (
            <div key={wf.id} className="flex items-center justify-between bg-gray-900/50 border border-gray-700/30 rounded-lg px-4 py-3">
              <div className="flex items-center gap-4">
                <StatusBadge status={wf.status} />
                <div>
                  <span className="text-white font-medium">{wf.title}</span>
                  <span className="text-gray-500 text-xs ml-2">{wf.content_type}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {wf.status !== 'live' && wf.status !== 'rejected' && (
                  <button onClick={() => advanceWorkflow(wf.id, wf.status)} className="flex items-center gap-1 text-green-400 hover:text-green-300 text-xs px-2 py-1 bg-green-500/10 rounded" data-testid={`advance-workflow-${wf.id}`}>
                    <ChevronRight size={12} /> Advance
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// === MAIN BROADCAST OS COMPONENT ===
const BroadcastOS = ({ authHeader }) => {
  const [dashboard, setDashboard] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('broadcast');
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/broadcast/dashboard`, authHeader);
      setDashboard(res.data);
    } catch (e) {
      console.error('Failed to fetch broadcast dashboard:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchDashboard(); }, []);

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400 flex items-center gap-2"><RefreshCw className="animate-spin" size={20} /> Loading Network Command...</div>
      </div>
    );
  }

  return (
    <div data-testid="broadcast-os-dashboard">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-black text-white">
            <span className="text-amber-500">CBX</span>TV NETWORK COMMAND
          </h1>
          <StatusBadge status={dashboard?.system_mode === 'demo' ? 'demo' : 'live'} size="lg" />
        </div>
        <p className="text-gray-500 text-sm">Broadcast operating system · Legal portal · Integration hub</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <SubTab label="Broadcast OS" active={activeSubTab === 'broadcast'} onClick={() => setActiveSubTab('broadcast')} icon={Radio} />
        <SubTab label="Legal Portal" active={activeSubTab === 'legal'} onClick={() => setActiveSubTab('legal')} icon={Shield} />
        <SubTab label="Connections" active={activeSubTab === 'connections'} onClick={() => setActiveSubTab('connections')} icon={Globe} />
        <SubTab label="Workflows" active={activeSubTab === 'workflows'} onClick={() => setActiveSubTab('workflows')} icon={Layers} />
      </div>

      {/* Tab content */}
      {activeSubTab === 'broadcast' && <BroadcastOSTab dashboard={dashboard} authHeader={authHeader} onRefresh={fetchDashboard} />}
      {activeSubTab === 'legal' && <LegalPortalTab dashboard={dashboard} authHeader={authHeader} />}
      {activeSubTab === 'connections' && <ConnectionsTab dashboard={dashboard} />}
      {activeSubTab === 'workflows' && <WorkflowTab dashboard={dashboard} authHeader={authHeader} />}
    </div>
  );
};

export default BroadcastOS;
